/**
 * Hover/focus tooltips for [data-tooltip] elements (the "?" icons next to labels).
 * A single fixed-position pop is appended to <body> so it never gets clipped by
 * scrolling modal content; delegated listeners cover dynamically added triggers.
 *
 * ## ⚠ THE POINTER MAY ENTER THE BUBBLE (owner ask, 2026-08-12)
 *
 * *"allow moving the mouse into tooltips without that causing them to be dismissed, as that's the
 * only way we can support clicking links inside them."* Exactly right, and it is what turns this
 * from a tooltip into a **toggletip** - a thing with content you can reach. The mechanics:
 *
 * - Leaving the trigger starts a SHORT close timer instead of hiding, and entering the bubble
 *   cancels it. The gap between the trigger and the bubble is 6px of dead space the pointer has to
 *   cross, so a timer is not a nicety - without it the tooltip closes mid-journey every time.
 * - The bubble is only interactive while it is open: `pointer-events` is off in the hidden state,
 *   or an invisible 300px box would sit over the page swallowing clicks.
 *
 * ⚠ **A link that exists ONLY inside a hover tooltip is unreachable by keyboard and by touch**, which
 * is why this is not just a hover change. The trigger is a real toggle: clicking or pressing it pins
 * the bubble open and moves focus into it, Escape closes it and puts focus back, and focus leaving
 * the bubble closes it. That is the whole reason `data-tooltip-action` is allowed to exist - moving
 * an anchor into a bubble nobody can open without a mouse would be a regression wearing a tidier
 * layout.
 *
 * ⚠ **The action is built from two DATA attributes and never from HTML.** `data-tooltip` has always
 * been plain text through `textContent`, and several of these strings interpolate values the owner
 * typed (an outlet's name, a release title). The moment this module parsed markup, every one of those
 * would become an injection site - so the anchor is `createElement("a")` with `textContent`, and
 * there is no path from a dictionary string to `innerHTML`.
 */

const SHOW_DELAY_MS = 150;
/**
 * How long the bubble survives the pointer leaving. It has to cover the 6px gap between the trigger
 * and the bubble - a pointer crossing it is briefly over neither - and to forgive the overshoot of
 * aiming at a 14px circle's dropdown.
 */
const HIDE_DELAY_MS = 260;
const VIEWPORT_MARGIN = 8;

let pop: HTMLDivElement | null = null;
let showTimer: number | undefined;
let hideTimer: number | undefined;
let activeTrigger: Element | null = null;
/** Pinned = opened by click/Enter rather than by hover, so it ignores the pointer leaving */
let pinned = false;

function ensurePop(): HTMLDivElement {
	if (!pop) {
		pop = document.createElement("div");
		pop.className = "tooltip-pop hidden";
		pop.setAttribute("role", "tooltip");
		// Entering the bubble cancels the close the pointer's departure scheduled; leaving it starts
		// one again. Bound once, on the element itself - the pop is outside every shadow root, so the
		// delegated listeners below never see it.
		pop.addEventListener("mouseenter", cancelHide);
		pop.addEventListener("mouseleave", () => scheduleHide());
		// ⚠ The pop lives in <body>, outside every shadow root AND outside every island, so none of
		// the delegated listeners below ever see it. Its own keyboard exits are bound here or they
		// do not exist: Escape from inside the bubble, and focus leaving it entirely.
		pop.addEventListener("keydown", e => {
			if (e.key !== "Escape") return;
			const trigger = activeTrigger;
			hide();
			if (trigger instanceof HTMLElement) trigger.focus();
		});
		pop.addEventListener("focusout", e => {
			// Only when focus has left the bubble for good - moving between two nodes inside it is
			// not an exit, and `relatedTarget` is null when focus goes nowhere at all.
			const next = e.relatedTarget;
			if (next instanceof Node && pop?.contains(next) === true) return;
			hide();
		});
		document.body.appendChild(pop);
	}
	return pop;
}

/**
 * The in-app navigator, injected at boot rather than imported.
 *
 * ⚠ `tooltip.ts` is imported by `GhElement`, which every page island extends, so a static import of
 * the router here closes a cycle (tooltip → router → pages → ghElement → tooltip). `app.ts` already
 * holds both and is where the wiring belongs.
 */
let navigate: ((page: string, subPath?: string[]) => void) | null = null;

export function setTooltipNavigator(fn: (page: string, subPath?: string[]) => void): void {
	navigate = fn;
}

/**
 * The text, with the dictionary's bold mark drawn rather than printed (round 324): a double-asterisk run
 * becomes a strong NODE and the rest stays text - the same one-mark grammar as emphasized(), which
 * is how the rest of the app reads these strings. The drip tooltip printed "**approved**" with its
 * asterisks until now. ⚠ Nodes, never markup, for the reason in the header - a string that
 * interpolates an owner's value must have no path to innerHTML. ⚠ Only the double mark: a single
 * asterisk is German's gender star ("Autor*innen") as often as it is emphasis.
 */
function fillText(el: HTMLElement, text: string): void {
	el.replaceChildren(
		...text.split("**").map((part, i) => {
			if (i % 2 === 0) return document.createTextNode(part);
			const strong = document.createElement("strong");
			strong.textContent = part;
			return strong;
		})
	);
}

function showFor(trigger: Element): void {
	const data = (trigger as HTMLElement).dataset;
	const text = data.tooltip;
	if (!text) return;
	cancelHide();
	activeTrigger = trigger;
	const el = ensurePop();
	fillText(el, text);

	// The optional action: a link the reader can actually click, which is the whole point of the
	// bubble being hoverable. Built as nodes, never as markup - see the module header.
	const label = data.tooltipAction;
	const page = data.tooltipPage;
	if (label != null && label !== "" && page != null && page !== "") {
		const sub = (data.tooltipSub ?? "").split("/").filter(part => part !== "");
		const link = document.createElement("a");
		link.className = "tooltip-action";
		link.textContent = label;
		// A real href, so it looks and behaves like a link (middle-click, copy address) - and a click
		// handler, because this router navigates through `navigateTo` and an href alone would change
		// the address and load nothing.
		link.href = `/${[page, ...sub].join("/")}`;
		link.addEventListener("click", e => {
			if (navigate == null) return;
			e.preventDefault();
			hide();
			navigate(page, sub);
		});
		el.appendChild(link);
	}

	el.classList.remove("hidden");

	const rect = trigger.getBoundingClientRect();
	const popRect = el.getBoundingClientRect();
	let top = rect.bottom + 6;
	if (top + popRect.height > window.innerHeight - VIEWPORT_MARGIN) {
		top = rect.top - popRect.height - 6;
	}
	let left = rect.left + rect.width / 2 - popRect.width / 2;
	left = Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - popRect.width - VIEWPORT_MARGIN));
	el.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`;
	el.style.left = `${left}px`;
}

/** Does this bubble hold something worth reaching? Only those are worth pinning or delaying for. */
function hasAction(): boolean {
	return pop?.querySelector(".tooltip-action") != null;
}

function cancelHide(): void {
	window.clearTimeout(hideTimer);
	hideTimer = undefined;
}

/**
 * Close, but not yet - the pointer may be on its way into the bubble.
 *
 * ⚠ A bubble with nothing to click closes immediately, as it always did. The delay exists to make an
 * ACTION reachable, and paying it on all ~200 plain tooltips would make the app feel sticky for a
 * gain none of them can offer.
 */
function scheduleHide(): void {
	if (pinned) return;
	if (!hasAction()) {
		hide();
		return;
	}
	cancelHide();
	hideTimer = window.setTimeout(hide, HIDE_DELAY_MS);
}

function hide(): void {
	window.clearTimeout(showTimer);
	showTimer = undefined;
	cancelHide();
	activeTrigger = null;
	pinned = false;
	pop?.classList.add("hidden");
}

/**
 * The [data-tooltip] element under the pointer/focus, or null.
 *
 * Reads `composedPath()[0]`, NOT `e.target`: these listeners live on `document`, and when a
 * `[data-tooltip]` sits inside a component's shadow root (a "?" icon on a shadow-rooted island),
 * the event RETARGETS `e.target` to the shadow host as it crosses the boundary - so
 * `e.target.closest("[data-tooltip]")` walks the host's light ancestors and finds nothing. The
 * composed path's first entry is the real innermost node, and `.closest` from there stays inside
 * that shadow tree and finds the trigger. For a light-DOM trigger `composedPath()[0] === e.target`,
 * so this is a no-op there (all of today's triggers are light - this is the shadow-DOM burn-down's
 * enabler for the ones to come). These events are all composed (`mouseover`/`mouseout`/`focusin`),
 * so the path is populated across boundaries.
 */
function triggerUnder(e: Event): Element | null {
	// ⚠ Walk the WHOLE composed path rather than `closest()` from its first entry: `closest()` climbs
	// the node's own tree and STOPS at its shadow root, so a trigger outside that root is unreachable
	// from inside it. The path is the flattened tree - every host on the way out is in it - so this
	// finds an ancestor across any number of boundaries. Found on the plan wall's ⚠ badge (2026-08-04,
	// owner): the badge's only content is a `<gh-icon>`, so the deepest node under the pointer is a
	// `<path>` in the ICON's shadow root, and `closest()` from there could never see the wrapper two
	// levels out. Hovering the button worked and hovering the badge did not, which is the same
	// shape as the bug above - an icon-only control is the case that breaks.
	for (const node of e.composedPath()) {
		if (!(node instanceof Element)) continue;
		if (node.hasAttribute("data-tooltip")) return node;
	}
	return null;
}

/**
 * The delegated set, bound to one root. `document` gets it at boot; every shadow root gets its
 * own - and the second half is not belt-and-braces, it is the only way these events are seen at
 * all inside an island (2026-08-04, owner: "I've hovered it with the mouse forever").
 *
 * ⚠ **A `mouseover` whose target AND relatedTarget are in the same shadow tree is never dispatched
 * outside that tree.** The DOM spec truncates the event path there on purpose: letting it out would
 * tell the outer page that the pointer moved between two nodes it is not allowed to know about. So
 * a document-level listener sees a pointer ENTER an island and never sees it move around INSIDE
 * one - which is every hover that matters, because a `[data-tooltip]` is always a few nodes deep.
 *
 * That is why the plan wall's reason appeared over its ICON and nowhere else: `<gh-icon>` has its
 * own shadow root, so a move from the island's markup into the icon crosses a boundary and the
 * event escapes. The tooltip looked flaky rather than broken, which is why it survived so long.
 *
 * ⚠ **Round 88's fix was `composedPath()[0]`, and this is the same lesson one layer deeper: reading
 * the composed path is no help for an event that never arrives.** `focusin`/`focusout` carry a
 * relatedTarget too and are truncated by the identical rule, so keyboard traversal inside one
 * island needs this just as much as the pointer does.
 */
function bindTooltips(root: Document | ShadowRoot): void {
	root.addEventListener("mouseover", e => {
		const trigger = triggerUnder(e);
		if (!trigger || trigger === activeTrigger) return;
		window.clearTimeout(showTimer);
		cancelHide();
		showTimer = window.setTimeout(() => showFor(trigger), SHOW_DELAY_MS);
	});
	root.addEventListener("mouseout", e => {
		// ⚠ Leaving the trigger no longer HIDES, it schedules - the pointer may be travelling into
		// the bubble, across the 6px of dead space between the two.
		if (triggerUnder(e)) scheduleHide();
	});
	root.addEventListener("focusin", e => {
		const trigger = triggerUnder(e);
		if (trigger) showFor(trigger);
	});
	root.addEventListener("focusout", () => scheduleHide());
	// Scrolling (page or modal body) leaves the fixed pop stale - just dismiss it
	root.addEventListener("scroll", hide, { capture: true, passive: true });
	// ⚠ A click on the TRIGGER pins the bubble and moves focus into it, which is what makes an action
	// inside it reachable without a mouse; a click anywhere else dismisses, as before. Without this,
	// moving a link into a tooltip would delete it for every keyboard and touch user.
	root.addEventListener("click", e => {
		const trigger = triggerUnder(e);
		if (trigger == null) {
			hide();
			return;
		}
		window.clearTimeout(showTimer);
		if (activeTrigger !== trigger) showFor(trigger);
		const action = pop?.querySelector<HTMLAnchorElement>(".tooltip-action");
		if (action == null) {
			hide();
			return;
		}
		pinned = true;
		cancelHide();
		action.focus();
	});
	root.addEventListener("keydown", e => {
		if ((e as KeyboardEvent).key !== "Escape" || activeTrigger == null) return;
		// Focus goes back where it came from - a bubble that closes and leaves focus nowhere drops
		// a keyboard reader at the top of the document.
		const trigger = activeTrigger;
		hide();
		if (trigger instanceof HTMLElement) trigger.focus();
	});
}

const boundRoots = new WeakSet<ShadowRoot>();

/**
 * Called by `GhElement` for every shadow root the app opens. Idempotent per root, because an
 * element can be disconnected and reconnected (a page island is re-rendered into the shell on
 * every navigation) and a second binding would run every handler twice.
 */
export function bindTooltipsInShadowRoot(root: ShadowRoot): void {
	if (boundRoots.has(root)) return;
	boundRoots.add(root);
	bindTooltips(root);
}

export function initTooltips(): void {
	bindTooltips(document);
}
